import { LayerProps } from 'react-map-gl/mapbox';
import { SELECTED_PIN_SCALE } from './pinHighlight';

// Cluster colors aligned with CSS variables in variables.css
// MapBox GL doesn't support CSS vars, so hex values must match:
// --color-cluster-small: #9F7AEA, --color-cluster-medium: #7C3AED
// --color-cluster-large: #6B2BD9, --color-cluster-xlarge: #5A21B6
export const clusterLayer: LayerProps = {
  id: 'cluster',
  type: 'circle',
  source: 'restaurants-source',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': [
      'interpolate',
      ['linear'],
      ['get', 'point_count'],
      1, '#9F7AEA',   // cluster-small
      50, '#7C3AED',  // cluster-medium (primary purple)
      200, '#6B2BD9', // cluster-large
      500, '#5A21B6'  // cluster-xlarge
    ],
    'circle-radius': [
      'interpolate',
      ['exponential', 0.5],  // Slower growth curve - prevents huge circles
      ['get', 'point_count'],
      1, 15,      // Single/few restaurants
      10, 20,     // Small cluster
      50, 28,     // Medium cluster
      200, 38,    // Large cluster
      500, 45,    // Very large cluster (dense urban)
      1000, 50    // Massive cluster
    ]
  }
};

export const clusterCountLayer: LayerProps = {
  id: 'cluster-count',
  type: 'symbol',
  source: 'restaurants-source',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 16
  },
  paint: {
    'text-color': '#ffffff',
    'text-halo-color': '#000000',
    'text-halo-width': 1.5,
  }
};

export const unclusteredLayer: LayerProps = {
  id: 'restaurant',
  type: 'symbol',
  source: 'restaurants-source',
  filter: ['!', ['has', 'point_count']],
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-size': 0.075,
    'icon-anchor': 'bottom',
    'icon-allow-overlap': true,
    'text-field': ['get', 'name'],
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
    'text-anchor': 'top',
    'text-offset': [0, 0.5],
    'text-max-width': 8,
    'text-allow-overlap': false,
    'text-optional': true,
  },
  paint: {
    'text-color': '#fff',
    'text-halo-color': '#1e293b',
    'text-halo-width': 1.5,
    'text-halo-blur': 1,
  },
};

/**
 * The selected restaurant's pin, drawn on top of every other pin from its own
 * one-feature source (`selected-restaurant-source`), enlarged by
 * SELECTED_PIN_SCALE and anchored at the tip like a normal pin. It repeats the
 * pin's name label because the normal layer hides this restaurant while it is
 * highlighted. `icon-translate` is what the bounce animates (see pinHighlight.ts).
 */
export const SELECTED_PIN_LAYER_ID = 'selected-restaurant';
export const selectedPinLayer: LayerProps = {
  id: SELECTED_PIN_LAYER_ID,
  type: 'symbol',
  source: 'selected-restaurant-source',
  layout: {
    ...unclusteredLayer.layout,
    'icon-size': 0.075 * SELECTED_PIN_SCALE,
  },
  paint: {
    ...unclusteredLayer.paint,
    'icon-translate': [0, 0],
    // The bounce sets this every frame. Mapbox eases paint changes over 300ms by
    // default, which would restart on each frame and smear the hops; apply it directly.
    'icon-translate-transition': { duration: 0, delay: 0 },
    // Lift straight up on screen, not along the map's bearing.
    'icon-translate-anchor': 'viewport',
  },
};
